import {
  Link,
  Stack,
  Typography,
} from '@ace-clublabs/shared-component-library';
import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import ProgressBar from '@/components/shared/ProgressBar';
import AgentsSelection from '@/features/appointments/components/AgentsSelection';
import useAgentsAndTimeslots from '@/features/scrollFlow/pages/Calendar/hooks/useAgentsAndTimeslots';
import useLandingPageRedirection from '@/hooks/useLandingPageRedirection';
import useProgress from '@/hooks/useProgress';
import useResolvedZipCode from '@/hooks/useResolvedZipCode';
import { Page } from '@/lib/tagging';
import { mapCommPref } from '@/lib/tagging/commPrefMapping';
import useTagging from '@/lib/tagging/useTagging';
import useAppointmentStore from '@/store/AppointmentStore';
import useSessionStore from '@/store/SessionStore';
import { formatLocationTitle } from '../utils/locationSearchFormatting';
import LocationSearchBox from './LocationSearchBox';

type AgentSelectionLayoutProps = {
  continueButtonDisabled?: boolean;
  isRescheduleFlow?: boolean;
  onClickContinue: () => void;
};

const AgentSelectionLayout: React.FC<AgentSelectionLayoutProps> = ({
  continueButtonDisabled = false,
  isRescheduleFlow = false,
  onClickContinue,
}) => {
  const tagging = useTagging();
  const tagged = useRef(false);

  const selectedAppointmentType = useAppointmentStore(
    (state) => state.selectedAppointmentType,
  );
  const preloadedAgentSearchLocation = useAppointmentStore(
    (state) => state.preloadedAgentSearchLocation,
  );
  const agentSearchLocation = useAppointmentStore(
    (state) => state.agentSearchLocation,
  );
  const { agentsForSelectedTimeSlot, isLoading: isLoadingAgents } =
    useAgentsAndTimeslots();

  const selectedAgent = useAppointmentStore((state) => state.selectedAgent);
  const setSelectedAgent = useAppointmentStore(
    (state) => state.setSelectedAgent,
  );
  const setSelectedBranch = useAppointmentStore(
    (state) => state.setSelectedBranch,
  );
  const selectedDestination = useAppointmentStore(
    (state) => state.selectedDestination,
  );
  const searchedDestination = useAppointmentStore(
    (state) => state.searchedDestination,
  );
  const userSearch = useAppointmentStore((state) => state.userSearch);
  const contactInfo = useAppointmentStore((state) => state.contactInfo);
  const { sessionId, sessionSource } = useSessionStore();

  useLandingPageRedirection();
  useProgress(60);

  const navigate = useNavigate();

  const isInBranchAppointment = selectedAppointmentType === 'in-branch';
  const isOnlineAppointment = selectedAppointmentType === 'online';
  const isButtonDisabled =
    continueButtonDisabled || isLoadingAgents || !selectedAgent?.agentRecordId;
  const initialZipCode = useResolvedZipCode();

  const displayLocation = agentSearchLocation
    ? formatLocationTitle(agentSearchLocation)
    : preloadedAgentSearchLocation
      ? formatLocationTitle(preloadedAgentSearchLocation)
      : initialZipCode || '';

  const inBranchHeading = displayLocation
    ? `Showing agents near ${displayLocation}`
    : 'Type in your preferred city or ZIP code.';

  const locationPlaceholder = displayLocation || undefined;

  const showEmptyState =
    agentsForSelectedTimeSlot.length === 0 && !isLoadingAgents;

  useEffect(() => {
    if (!tagged.current) {
      const trvEndLocation =
        searchedDestination || userSearch || selectedDestination.name;
      tagging.tag('view', sessionSource, {
        page: Page.selectAgent,
        sessionid: sessionId,
        flow: isRescheduleFlow ? 'modify' : '',
        trv_end_location: trvEndLocation,
        comm_pref: mapCommPref(
          contactInfo.communicationPreference,
          isInBranchAppointment,
          isOnlineAppointment,
        ),
      });
      tagged.current = true;
    }
  }, [
    selectedDestination.name,
    sessionSource,
    sessionId,
    tagging.tag,
    contactInfo.communicationPreference,
    isInBranchAppointment,
    isOnlineAppointment,
    searchedDestination,
    userSearch,
    isRescheduleFlow,
  ]);

  useEffect(() => {
    if (isInBranchAppointment && selectedAgent?.location) {
      setSelectedBranch({
        branchId: selectedAgent.serviceTerritoryId ?? '',
        branchName: selectedAgent.location,
        branchAddress: '',
        distance: 0,
      });
    }
  }, [selectedAgent, isInBranchAppointment, setSelectedBranch]);

  const handleClickBack = () => {
    navigate(-1);
  };

  return (
    <Stack
      direction='column'
      sx={{
        height: '100%',
        width: '100%',
        backgroundColor: 'background.default',
      }}
    >
      <Stack
        spacing={4}
        sx={{
          alignItems: 'center',
          justifyContent: 'center',
          flex: 1,
          px: 2,
          pt: 4,
          overflow: 'hidden',
        }}
      >
        <Stack
          spacing={2}
          sx={{
            width: '100%',
            maxWidth: '600px',
            textAlign: 'left',
            minHeight: 0,
          }}
        >
          <Typography variant='h5'>
            {isInBranchAppointment
              ? inBranchHeading
              : 'Please select an agent below.'}
          </Typography>

          {isInBranchAppointment && (
            <LocationSearchBox placeholder={locationPlaceholder} />
          )}

          <Stack sx={{ flex: 1, minHeight: 0 }}>
            {showEmptyState ? (
              <Typography variant='body1'>
                No nearby agents are available for the selected time slot.
                Please try{' '}
                <Link onClick={handleClickBack}>
                  selecting a different time slot
                </Link>{' '}
                or adjust your location search criteria.
              </Typography>
            ) : (
              <AgentsSelection
                agents={agentsForSelectedTimeSlot}
                selectedAgent={selectedAgent}
                onSelectAgent={setSelectedAgent}
                isLoading={isLoadingAgents}
              />
            )}
          </Stack>
        </Stack>
      </Stack>

      <ProgressBar
        buttonDisabled={isButtonDisabled}
        displayBackButton={true}
        onClickContinue={onClickContinue}
      />
    </Stack>
  );
};

export default AgentSelectionLayout;
